import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { writeBarcode } from "@/controls/tag-label/zxing";
import { shownTagNo } from "@/pages/sawlines/utils/entryBarcode";
import {
	changed,
	type Field,
	fmtLbs,
	fmtLen,
	type SectionMap,
	tagQrText,
	type Values,
	weightLbs,
} from "../tagStation";

const useQr = (text: string) =>
	useQuery<string, Error>({
		queryKey: ["tagQr", text],
		queryFn: async () => {
			const result = await writeBarcode(text, { format: "QRCode" });
			if (result.error) throw new Error(result.error);
			return `data:image/svg+xml;utf8,${encodeURIComponent(result.svg)}`;
		},
		staleTime: Number.POSITIVE_INFINITY,
		retry: false,
	});

// 4" I BEAM @ 3.25 -> 4" @3.25, as on the plant's tags
const shortSection = (v: Values, sections: SectionMap) => {
	const s = sections[v.section];
	if (!s?.Description || s.LBsPerFoot == null) return s?.Description ?? "";
	return `${s.Description.split(" ")[0]} @${Number(s.LBsPerFoot.toFixed(2))}`;
};

export interface TagProps {
	values: Values;
	// null = not printed yet
	tagNo: string | null;
	scrap: boolean;
	sections: SectionMap;
	// print date; none = today
	date?: string | null;
	// what arrived: values that differ are highlighted, with what it was
	base?: Values;
	baseLabel?: string;
	width?: string;
}

export const TagPreview = ({
	values: v,
	tagNo,
	scrap,
	sections,
	date,
	base,
	baseLabel = "Mill sent",
	width = "460px",
}: TagProps) => {
	const qr = useQr(tagQrText(tagNo ?? "0000000", v)).data;
	const diff = base ? changed(base, v) : [];
	const mark = (tip: string, fields: Field[], children: ReactNode) =>
		base && fields.some((f) => diff.includes(f)) ? (
			<Tooltip title={tip} arrow>
				<Box component="b" tabIndex={0} sx={{ bgcolor: "#FFE58A" }}>
					{children}
				</Box>
			</Tooltip>
		) : (
			<b>{children}</b>
		);
	const qrImg = (
		<Box
			component="img"
			src={qr}
			alt=""
			sx={{ width: "3.4em", height: "3.4em", display: "block" }}
		/>
	);

	return (
		<Box
			sx={{
				containerType: "inline-size",
				width: "100%",
				display: "flex",
				justifyContent: "center",
			}}
		>
			<Box
				role="img"
				aria-label={`Tag ${tagNo ? shownTagNo(tagNo) : "preview"}`}
				sx={{
					"--tw": `min(100cqw, ${width})`,
					width: "var(--tw)",
					fontSize: "calc(var(--tw) / 27)",
					bgcolor: "#fff",
					color: "#111",
					fontFamily: "Arial, Helvetica, sans-serif",
					p: ".55em .9em",
					display: "grid",
					gridTemplateRows: "auto 1fr auto",
					gap: ".2em",
					position: "relative",
					aspectRatio: "1.43 / 1",
					border: "1.5px solid #222",
					borderRadius: ".8em",
					boxShadow: "0 2px 10px rgba(0,0,0,.08)",
					"& b": { fontWeight: 400 },
				}}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "auto 1fr auto",
						alignItems: "center",
						gap: ".6em",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: ".5em" }}>
						{/* punched hole of the stock: not printed */}
						<Box
							sx={{
								width: "1.2em",
								height: "1.2em",
								borderRadius: "50%",
								bgcolor: "#bdbdbd",
							}}
						/>
						<Box sx={{ fontWeight: 700, fontSize: "1.1em", lineHeight: 1.1 }}>
							SWVA
							<Box sx={{ fontSize: ".45em", fontWeight: 400, color: "#444" }}>
								SWVA, Inc.
							</Box>
						</Box>
					</Box>
					<Box
						sx={{
							textAlign: "center",
							fontSize: ".8em",
							lineHeight: 1.35,
							color: "#444",
						}}
					>
						{dxtToLocalServerTime(date || new Date(), "MM/dd/yy")}
						<Box
							sx={{
								fontSize: "1.3em",
								letterSpacing: ".06em",
								color: "#111",
							}}
						>
							{tagNo ? shownTagNo(tagNo) : "– – –"}
						</Box>
					</Box>
					{qrImg}
				</Box>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "auto 1.4fr auto 1fr",
						gap: ".35em .45em",
						alignItems: "end",
						alignContent: "center",
						"& > span": { color: "#333", fontSize: ".95em" },
						"& > b": {
							display: "block",
							borderBottom: "1px solid #222",
							fontSize: "1.2em",
							px: ".2em",
							textAlign: "center",
							whiteSpace: "nowrap",
							minHeight: "1.2em",
						},
					}}
				>
					<span>Length</span>
					{mark(
						`${baseLabel} ${base && fmtLen(base.lengthIn)}`,
						["len"],
						fmtLen(v.lengthIn),
					)}
					<span>Qty</span>
					{mark(`${baseLabel} ${base?.pieces}`, ["pcs"], v.pieces)}
					<span>Heat</span>
					{mark(`${baseLabel} ${base?.heat || "nothing"}`, ["heat"], v.heat)}
					<span>Weight</span>
					{mark(
						`Was ${base && fmtLbs(weightLbs(base, sections))}`,
						["pcs", "len", "section"],
						fmtLbs(weightLbs(v, sections)),
					)}
				</Box>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "auto 1fr 1fr",
						alignItems: "center",
						gap: ".5em",
						"& > div": { textAlign: "center", lineHeight: 1 },
						"& > div > b": { fontSize: "2.2em", display: "inline-block" },
						"& small": { fontSize: ".65em", color: "#333", display: "block" },
					}}
				>
					{qrImg}
					<div>
						{mark(`${baseLabel} ${base?.section}`, ["section"], v.section)}
						<small>{shortSection(v, sections)}</small>
					</div>
					<div>
						{mark(
							`${baseLabel} ${base?.grade || "nothing"}`,
							["grade"],
							v.grade || " ",
						)}
						<small>Grade</small>
					</div>
				</Box>
				{scrap && (
					// free spot under Weight: never over data
					<Box
						sx={{
							position: "absolute",
							inset: "62% 3.6% 22% 57.5%",
							display: "grid",
							placeItems: "center",
							border: ".13em solid #d32f2f",
							borderRadius: ".14em",
							color: "#d32f2f",
							fontWeight: 700,
							fontSize: "2.3em",
							lineHeight: 1,
							letterSpacing: ".12em",
							textIndent: ".12em",
							opacity: 0.92,
							pointerEvents: "none",
						}}
					>
						SCRAP
					</Box>
				)}
			</Box>
		</Box>
	);
};
